import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@progress/kendo-react-buttons';
import _ from 'lodash';
import AutoComplete from './Autocomplete';
import Grid from '../dataGrid/DataGrid';
import Dialog from '../dialogs/Dialog';
import MetaGrid from '../meta/MetaGrid';
import eventEmitter from '../utils/eventEmitter';
import withMeta from '../utils/withMeta';
import flags from '../dataGrid/flags';
import { processFilter } from '../dataGrid/gridHelper';
import { processAfter, processEnumsAfter, getPrimaryField } from '../utils/helper';
import { usePrevious } from '../utils/hooks';
import { useValidation } from '../utils/ValidationProvider';
import './Picker.scss';

const buildFilter = (selected, textField, required, allDataItem) => {
  if (!selected || (required && allDataItem && allDataItem === selected)) {
    return {
      filters: []
    };
  }

  return {
    logic: 'and',
    filters: [
      {
        field: textField,
        operator: "contains",
        value: _.get(selected, textField.split('.'))
      }
    ]
  };
};

const autocompleteHasValue = (filter, textField) => {
  return filter?.filters && filter.filters.length === 1 && filter.filters[0].field === textField && filter.filters[0].value;
};

let _filter = { filters: [] };
let _page = { skip: 0, take: 100 };
let _sort = [];
let _flag;

/**
 * Picker component
 * @module
 * @param {string} name - Name
 * @param {string} title - Picker title
 * @param {string} textField - Text field
 * @param {func} onChange - Function invoked on selected change
 * @param {func} [onSubmit] - Function invoked on sumbit
 * @param {array} data - All data for picker
 * @param {bool} isLoading - Loading
 * @param {func} onSelect - Function invoked on item select
 * @param {object} [selected] - Current selected item
 * @param {number} total - Total number of items
 * @param {array} schema - Schema for picker
 * @param {bool} [required] - Is picker required?
 * @param {bool} [disabled] - Is picker readonly?
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {string} [primaryField] - Primary field
 * @param {func} [onOpened] - Function invoked on picker open
 * @param {object} [initFilter] - Filter, can be canceled
 * @param {object} [fixedFilter] - Filter, can't be canceled
 * @param {bool} [autofocus] - Should picker be focused?
 * @param {string} [placeholder] - Picker placeholder
 * @param {any} [detail] - Detail, create new record (null = without this function)
 * @param {bool} [editableDetail] - Editable detail, requires onSubmit and detail props
 * @param {func} [onAdd] - Function invoked on add entry
 * @param {number} [tabIndex] - Picker tab index
 * @param {bool} [isOpen] - Is picker open?
 * @param {func} [openPicker] - Function invoked on picker open
 * @param {func} [closePicker] - Function invoked on picker close
 * @param {string} [db] - db
 * @param {array} [sort] - Array of sorts for AutoComplete component
 * @param {resetValue} [resetValue] - Shoud picker reset value with fixedFilter?
 */

const PickerComponent = (props) => {
  const { primaryField, parentProps, reducer, required, allDataItem, name, onSelect, onBlur } = props;

  const filterRef = useRef(_filter);
  const pageRef = useRef(_page);
  const sortRef = useRef(_sort);

  const [isOpen, setIsOpen] = useState(props.isOpen);
  const [initFilter, setInitFilter] = useState(props.initFilter || { filters: [] });
  const [selected, setSelected] = useState(props.selected);
  const [fixedFilter, setFixedFilter] = useState(props.fixedFilter || { filters: [] });
  const [metaFilters, setMetaFilters] = useState([]);
  const [metaSorts, setMetaSorts] = useState([]);
  const [schema, setSchema] = useState(props.schema);
  const previous = usePrevious({ fixedFilter: props.fixedFilter, initFilter: props.initFilter });
  
  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);

  const { validate, setValidationValue } = useValidation(field, props.selected);

  const init = useCallback(async () => {
    if (props.withMeta) {
      const f = await props.meta.getFilters(props.identif || props.primaryField, schema, null, props.db);
      f.forEach(e => {
        e.filters.forEach(filter => {
          if (!(typeof filter.value === 'string' || filter.value instanceof String))
            return;
          let toReplace = filter.value.match(/{([^}]+)}/g);
          if (!toReplace) {
            return;
          }
          if (parentProps.forPodr) {
            let finalString = filter.value;
            toReplace.forEach((element) => {
              let propName = element.replace(/[{}]/g, "");
              const replaceWith = _.get(parentProps.forPodr, propName);
              finalString = finalString.replace(
                element,
                replaceWith != null ? replaceWith : ""
              );
            });
            filter.value = finalString;
          }
        });
      });
      setMetaFilters(f);
      const s = await props.meta.getSort(props.identif || props.primaryField, schema, props.db);
      setMetaSorts(s);
    }


    // eslint-disable-next-line
  }, [props.primaryField, props.db, props.identif, props.withMeta, JSON.stringify(schema)]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    setSchema(props.schema);
  }, [props.schema]);

  useEffect(() => {
    if (props.withMeta) {
      setFixedFilter({ filters: [...(props.fixedFilter ? props.fixedFilter.filters : []), ...metaFilters.filter(x => !x.disabled && !x.CanDisabled).map(x => x.filters).flat()] });
    }
    // eslint-disable-next-line
  }, [props.withMeta, JSON.stringify(props.fixedFilter), metaFilters]);

  useEffect(() => {
    if (props.withMeta) {
      setInitFilter(prev => props.initFilter || { filters: [...(prev?.filters ?? []), ...metaFilters.filter(x => !x.disabled && x.CanDisabled).map(x => x.filters).flat()] });
    }
    // eslint-disable-next-line
  }, [props.withMeta, JSON.stringify(props.initFilter), metaFilters]);

  useEffect(() => {
    if (!props.withMeta) {
      setFixedFilter(props.fixedFilter || { filters: [] });
      const f = _.get(props.selected, props.textField.split('.')) ? buildFilter(props.selected, props.textField, props.required, props.allDataItem) : props.initFilter;
      setInitFilter(f);
    }
    // eslint-disable-next-line
  }, [props.textField, props.required, props.withMeta, JSON.stringify(props.initFilter), JSON.stringify(props.fixedFilter), JSON.stringify(props.selected), JSON.stringify(props.allDataItem), metaFilters]);

  // nahradza povodny getDerivedStateFromProps()
  useEffect(() => {
    if (
      !_flag &&
      selected !== props.selected &&
      !(autocompleteHasValue(initFilter, props.textField) && props.selected && selected === null)
    ) {
      const f = buildFilter(props.selected, props.textField, props.required, props.allDataItem);
      setInitFilter(f);
      setSelected(props.selected);
    }
    // eslint-disable-next-line
  }, [JSON.stringify(props.selected), JSON.stringify(initFilter), JSON.stringify(props.allDataItem), props.textField, props.required]);

  useEffect(() => {
    if (!isOpen && !_.isEqual(selected, props.selected)) dataCheck();
    if (props.selected === null) {
      setInitFilter(props.initFilter || { filters: [] });
    }
    // eslint-disable-next-line
  }, [JSON.stringify(props.selected)]);

  const resetValue = useCallback(async () => {
    const dataPrimaryField = getPrimaryField(props.primaryField);
    if (props.withMeta && props.resetValue && _.isEqual(selected, props.selected) &&
      ((props.fixedFilter && props.fixedFilter.filters.length > 0) || (props.initFilter && props.initFilter.filters.length > 0)) &&
      JSON.stringify(previous) !== JSON.stringify({ fixedFilter: props.fixedFilter, initFilter: props.initFilter })
    ) {
      const f = {
        filters: [
          ...(props.fixedFilter ? props.fixedFilter.filters : []), ...metaFilters.filter(x => !x.disabled && !x.CanDisabled).map(x => x.filters).flat(),
          ...(props.initFilter ? props.initFilter.filters : metaFilters.filter(x => !x.disabled && x.CanDisabled).map(x => x.filters).flat())
        ]
      };
      let res = null;
      if (props.selected && (!props.allDataItem || (props.allDataItem && _.isEqual(props.allDataItem, props.selected)))) {
        const fClear = { ...f, filters: [...f.filters, { field: dataPrimaryField, operator: "eq", value: props.selected[dataPrimaryField] } ] };
        res = await props.meta.getList(props.identif || props.primaryField, fClear, [], { skip: 0, take: 2 }, props.db);
        if (res.Data && res.Data.TotalRecords === 0 && !_.isEqual(props.allDataItem ? props.allDataItem : null, props.selected)) {
          await onSelect(props.name, props.allDataItem ? props.allDataItem : null);
        }
      }
      res = await props.meta.getList(props.identif || props.primaryField, f, [], { skip: 0, take: 2 }, props.db);
      if (res.Data && res.Data.TotalRecords === 1 && (!props.selected || (props.selected && !_.isEqual(res.Data.Items[0][dataPrimaryField], props.selected[dataPrimaryField])))) {
        await onSelect(props.name, res.Data.Items[0]);
      }
    }

    // eslint-disable-next-line
  }, [props.identif, props.primaryField, props.withMeta, props.resetValue, props.db, props.name, props.allDataItem, JSON.stringify(props.fixedFilter), JSON.stringify(props.initFilter)]);

  useEffect(() => {
    resetValue();
  }, [resetValue]);

  const handleOpen = (event) => {
    event.preventDefault();
    setIsOpen(true);
    if (props.initFilter && initFilter.filters.length === 0) {
      setInitFilter(props.initFilter);
    }
    if (props.openPicker !== undefined) {
      props.openPicker();
    }

    if (props.onOpened) {
      props.onOpened();
    }
  };

  const handleClose = () => {
    _flag = false;
    if (props.closePicker !== undefined) {
      props.closePicker();
    }
    setIsOpen(false);
  };

  const handleFilter = (filter) => {
    setInitFilter(filter);
  };

  const handleSelect = (selected) => {
    _flag = true;
    setSelected(selected[0]);
  };

  const handleConfirm = async (event) => {
    _flag = false;
    const newSelected = event.dataItem || selected;
    const f = buildFilter(newSelected, props.textField, props.required, props.allDataItem);
    setInitFilter(f);
    setIsOpen(false);
    await onSelect(props.name, newSelected);
    setValidationValue(newSelected);
    validate();
  };

  const dataCheck = useCallback(() => {
    if (!props.selected || Object.keys(props.selected).length === 0) {
      setSelected(null);
    }
  }, [props.selected]);

  const handleSelectAndConfirm = useCallback(async (selected, nameParam, e) => {
    const item = required && allDataItem && !selected && e.nativeEvent.type === "click" && e.target.value === '' ? allDataItem : selected;
    setSelected(item);
    await onSelect(name, item);
    setValidationValue(item);
  }, [required, allDataItem, name, onSelect, setValidationValue]);

  const handleBlur = useCallback(() => {
    validate();
    dataCheck();
    if (onBlur) {
      onBlur();
    }
  }, [onBlur, validate, dataCheck]);

  const handleCreate = useCallback(async (item) => {
    await handleSelectAndConfirm(item);
    const f = buildFilter(item, props.textField);
    setInitFilter(f);
    setIsOpen(false);
    handleBlur();
  }, [handleSelectAndConfirm, handleBlur, props.textField]);

  const localProcessFilter = (filter) => {
    return processFilter(filter, fixedFilter);
  };

  // const applyFilter = (id) => {
  //   props.onChange({ filters: [{ field: props.primaryField, operator: "eq", value: id }] }, [], { skip: 0, take: 1 });
  //   handleFilter({ filters: [{ field: props.primaryField, operator: "eq", value: id }] });
  //   if (props.onSubmit) {
  //     props.onSubmit();
  //   }
  //   eventEmitter.changeTab(props.primaryField, 0);
  // };

  const onChange = (filter, sort, page) => {
    const margeFilter = localProcessFilter(filter);
    filterRef.current = margeFilter;
    pageRef.current = page;
    sortRef.current = sort;
    if (filter) {
      props.onChange(margeFilter, sort, page, props.identif);
    } else {
      let page = sessionStorage.Page ? JSON.parse(sessionStorage.Page) : [0, 5];
      props.onChange(margeFilter, "", page, props.identif);
      eventEmitter.changeTab(props.identif, 0);
    }
  };

  const handleRefresh = () => {
    props.onChange(filterRef.current, sortRef.current, pageRef.current, props.identif);
  };

  // const onAdd = async () => {
  //   if (props.editableDetail) {
  //     await props.onSubmit(0);
  //   }
  //   if (props.onAdd) {
  //     await props.onAdd();
  //   }
  //   eventEmitter.changeTab(props.primaryField, 1);
  // };

  // const onRowDoubleClick = async () => {
  //   if (props.editableDetail) {
  //     let id = selected[props.primaryField];
  //     await props.onSubmit(id);
  //     eventEmitter.changeTab(props.primaryField, 1);
  //   }
  // };

  // const getPrimaryField = () => {
  //   return getPrimaryField(props.primaryField);
  // };

  /**
  * Metoda buildFooter
  * vráti element, ktorý sa má vyrenderovať pod dropdownlistom
  * default zobrazuje počet zobrazených záznamov a celkým počet záznamov
  */

  const buildFooter = () => {
    let text = props.data && props.data.length > 0 ? `Zobrazených ${props.data.length} z ${props.total} záznamov` : "";
    return (
      <div className="autocomplete-footer" title="Pre zobrazenie nezobrazených výsledkov spresnite podmienku vyhľadávania alebo použite dialóg.">
        <div className="autocomplete-footer-text">
          {text}
        </div>
      </div>
    );
  };

  const handleReducer = useCallback(async (action, data, state, frmState) => {
    switch (action) {
      case "GET_SCHEMA":
        state = { ...state, akc: { ...state.akc, hidden: state.akc.hidden | flags.ONLY_SELECT, disabled: state.akc.disabled | flags.ONLY_SELECT } };
        state = state;
        break;
      case "SELECT":
        if (primaryField === state.primaryField) {
          handleSelect(state[state.primaryField]?.selected);
        }
        break;
      case "CREATE":
        if (primaryField === state.primaryField) {
          await handleCreate(data.data);
        }
        break;
      default:
        break;
    }
    if (reducer) {
      return await reducer(action, data, state, frmState);
    } else {
      return state;
    }
  }, [handleCreate, parentProps, primaryField, reducer]);

  const data = props.data
    .map(x => { return processAfter(schema, x); })
    .map(x => { return processEnumsAfter(schema, x); });

  return (
    <div className={`form-input picker ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
      <AutoComplete
        textField={props.textField}
        valueField={props.valueField}
        primaryField={props.primaryField}
        selected={props.selected}
        onChange={(filter, sort, page) => onChange(localProcessFilter(filter), sort, page)}
        data={data}
        isLoading={props.isLoading}
        onSelect={handleSelectAndConfirm}
        onBlur={handleBlur}
        filter={initFilter}
        onFilter={handleFilter}
        required={props.required}
        disabled={props.disabled}
        validationMessage={props.validationMessage}
        autofocus={props.autofocus}
        placeholder={props.placeholder}
        tabIndex={props.tabIndex}
        footer={buildFooter()}
        allDataItem={props.allDataItem}
        sort={props.sort || metaSorts}
        setValidation={setValidationValue}
        name={props.name}
      />
      <Button
        type="button"
        onClick={handleOpen}
        icon="more-vertical"
        disabled={props.disabled}
        data-testid={"openPickerButton"}
      />
      {isOpen && <Dialog
        isOpen={isOpen}
        title={props.title}
        onClose={handleClose}
        onConfirm={handleConfirm}
        confirmDisabled={!selected}
        className={props.detail ? null : "picker-dialog"}
      >
        {props.detail ?
          <MetaGrid
            primaryField={props.primaryField}
            //identif={props.identif}
            componentFactory={() => { return null; }}
            akcFactory={props.akcFactory}
            reducer={handleReducer}
            initFilter={initFilter}
            fixedFilter={fixedFilter}
            db={props.db}
          />
          :
          <Grid
            total={props.total}
            data={data}
            isLoading={props.isLoading}
            onChange={onChange}
            schema={schema}
            onSelect={handleSelect}
            initSort={props.sort || metaSorts}
            onFilter={handleFilter}
            primaryField={props.primaryField}
            onConfirm={handleConfirm}
            identif={props.identif}
            isPicker
            parentProps={props.parentProps}
            currentDataItem={props.currentDataItem}
            hidden={flags.PRINT | flags.RESET | flags.ADD | flags.COPY | flags.DELETE | flags.CANCEL | flags.EDITCELL | flags.USER_FILTER | flags.FULLTEXT_SEARCH}
            withMeta={props.withMeta}
            fixedFilter={fixedFilter}
            db={props.db}
            filter={filterRef.current}
            sort={sortRef.current}
            page={pageRef.current}
            initFilter={initFilter}
            selected={selected ? [selected] : []}
            hiddenBack={true}
            onGetList={() => { }}
            onGet={() => { }}
            onChangeSchema={(newSchema) => { setSchema(newSchema); }}
            updateSort={() => { }}
            userFilterExtended={[]}
            onRowDoubleClick={handleConfirm}
            onRefresh={handleRefresh}
            name={props.name}
          />
        }
      </Dialog>}
    </div>
  );

};

PickerComponent.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  textField: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  selected: PropTypes.object,
  total: PropTypes.number.isRequired,
  schema: PropTypes.array.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  validationMessage: PropTypes.string,
  primaryField: PropTypes.string,
  onOpened: PropTypes.func,
  //initFilter - filter, ktorý je clear-nuteľný
  initFilter: PropTypes.object,
  //fixedFilter - filter, ktorý nie je možné odstrániť
  fixedFilter: PropTypes.object,
  autofocus: PropTypes.bool,
  placeholder: PropTypes.string,

  //Vytvorenie nového záznamu v pickeri (null = bez tejto možnosti)
  detail: PropTypes.bool,
  reducer: PropTypes.func,
  onAdd: PropTypes.func,
  tabIndex: PropTypes.number,
  isOpen: PropTypes.bool,
  openPicker: PropTypes.func,
  closePicker: PropTypes.func,
  editableDetail: PropTypes.bool,
  withMeta: PropTypes.bool,
  parentProps: PropTypes.object,
  allDataItem: PropTypes.object,
  db: PropTypes.string,
  sort: PropTypes.array,
  resetValue: PropTypes.bool
};

export default withMeta(PickerComponent);